import classNames from 'classnames'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGame } from '../../../../../../../../contexts/GameContext'
import styles from './CannedMessages.module.css'
import { getIcon } from '../../../../../../../../common/components/icons/utils'
import { Button } from '../../../../../../../../common/components/button/Button'
import { CannedMessageList } from './CannedMessageList'
import { CannedMessageEditor } from './CannedMessageEditor'

type CannedMessagesProps = {
  gameId?: number
  setInputValue: (inputValue: string) => void
}

export const CannedMessages: React.FC<CannedMessagesProps> = ({ gameId, setInputValue }) => {
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = useState<boolean>(true)
  const [toBeCannedValue, setToBeCannedValue] = useState('')
  const [toBeCannedId, setToBeCannedId] = useState<number | null>(null)
  const [showGame, setShowGame] = useState<boolean>(true)
  const [showCannedMessageEditor, setShowCannedMessageEditor] = useState(false)
  const { cannedUserMessages, deleteCannedUserMessage, cannedGameMessages, deleteCannedGameMessage } = useGame()

  const toggleIsExpanded = () => setIsExpanded((prev) => !prev)

  const handleSetShowGameOrPersonal = (state: boolean) => {
    setShowGame(state)
    setToBeCannedId(null)
  }

  const handleEditMessage = (id: number, text: string) => {
    setToBeCannedId(id)
    const messages = showGame ? cannedGameMessages : cannedUserMessages
    let message = text
    messages.forEach((cannedMessage) => {
      if (id === cannedMessage.id && cannedMessage.default) {
        message = t(cannedMessage.text)
      }
    })

    handleSetToBeCannedValue(message)
    setShowCannedMessageEditor(true)
  }

  const handleDeleteMessage = (id: number) => {
    if (showGame) deleteCannedGameMessage(id)
    else deleteCannedUserMessage(id)
  }

  const handleSelectMessage = (text: string) => {
    setInputValue(text)
  }

  const handleSetToBeCannedValue = (text: string) => {
    setToBeCannedValue(text)
    if (text.trim().length === 0) {
      setToBeCannedId(null)
    }
  }

  const handleCloseEditor = () => {
    setToBeCannedId(null)
    handleSetToBeCannedValue('')
    setShowCannedMessageEditor(false)
  }

  return (
    <div className={classNames(styles.cannedMessagesContainer, 'tiny')}>
      <div className={styles.cannedMessagesHeader}>
        <span onClick={toggleIsExpanded}>
          <label className={classNames(styles.label)}>{t('canned_messages.title', 'Saved feedback')}</label>
        </span>
        <button className={classNames(isExpanded && styles.iconExpanded)} type='button' onClick={toggleIsExpanded}>
          {getIcon('arrowDown')}
        </button>
      </div>
      {isExpanded && (
        <div className={styles.cannedMessagesContent}>
          <div className={styles.tabsContainer}>
            <button
              className={classNames(styles.tab, showGame && styles.selectedTab)}
              type='button'
              onClick={() => handleSetShowGameOrPersonal(true)}
            >
              {t('canned_messages.tabs.game', 'Game')}
            </button>
            <button
              className={classNames(styles.tab, !showGame && styles.selectedTab)}
              type='button'
              onClick={() => handleSetShowGameOrPersonal(false)}
            >
              {t('canned_messages.tabs.personal', 'Personal')}
            </button>
          </div>
          <div className={styles.selectedTabContainer}>
            <div className={styles.infoContainer}>
              <div className={styles.infoIcon}>{getIcon('info')}</div>
              <p className={styles.infoMessage}>
                {showGame
                  ? t(
                      'canned_messages.game_tab_info',
                      'Saved messages on the game tab are shared with all the instructors of the game',
                    )
                  : t(
                      'canned_messages.personal_tab_info',
                      'Saved messages on personal tab are visible for you in all the games you instruct',
                    )}
              </p>
            </div>
            <CannedMessageList
              editMessage={handleEditMessage}
              deleteMessage={handleDeleteMessage}
              selectMessage={handleSelectMessage}
              messages={showGame ? cannedGameMessages : cannedUserMessages}
            />
            {showCannedMessageEditor && (
              <CannedMessageEditor
                isGame={showGame}
                onClose={handleCloseEditor}
                initialId={toBeCannedId}
                initialValue={toBeCannedValue}
              />
            )}
            <Button
              type='button'
              variant='borderless-tiny'
              onClick={() => setShowCannedMessageEditor(true)}
              tabIndex={0}
            >
              {t('canned_message.add_canned', 'Add new feedback')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
